import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ImagingReportFields,
  emptyImagingReportForm,
  imagingReportToForm,
} from "@/components/imaging/imaging-report-form";
import { printImagingReport } from "@/components/imaging/ImagingReportPrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  listImagingExams,
  saveImagingReport,
  updateImagingExamStatus,
} from "@/core/imaging/imaging-store";
import {
  formatImagingReportSummary,
  IMAGING_STATUS_LABELS,
  imagingExamConsultationLabel,
  type ImagingExam,
} from "@/core/imaging/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { notifyImagingReportReady } from "@/core/notifications/imaging-notify";
import { routes } from "@/router/all_routes";

export function ImagingReportsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reportForm, setReportForm] = useState(emptyImagingReportForm);
  const [fileName, setFileName] = useState("");

  const exams = useMemo(() => {
    void refreshKey;
    return listImagingExams();
  }, [refreshKey]);

  const awaitingReport = exams.filter(
    (e) => e.status === "in_progress" || e.status === "scheduled",
  );
  const withReport = exams.filter((e) => e.status === "completed" && e.reportText);

  const awaitingPagination = useClientPagination(awaitingReport, [], 25);
  const { pagedItems: pagedAwaiting } = awaitingPagination;

  const archivesPagination = useClientPagination(withReport, [], 25);
  const { pagedItems: pagedArchives } = archivesPagination;

  const selected = selectedId ? exams.find((e) => e.id === selectedId) ?? null : null;
  const refresh = () => setRefreshKey((k) => k + 1);

  const openExam = (exam: ImagingExam) => {
    setSelectedId(exam.id);
    setReportForm(
      imagingReportToForm({
        technique: exam.reportTechnique,
        findings: exam.reportFindings,
        conclusion: exam.reportConclusion,
      }),
    );
    setFileName(exam.reportFileName);
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type.startsWith("text/") || file.name.endsWith(".txt")) {
      const reader = new FileReader();
      reader.onload = () => {
        setReportForm((prev) => ({ ...prev, findings: String(reader.result ?? "") }));
        setFileName(file.name);
      };
      reader.readAsText(file);
      return;
    }

    setFileName(file.name);
    setReportForm((prev) => ({
      ...prev,
      findings: `[Fichier joint : ${file.name} — contenu binaire non affiché en démo]\n\nSaisissez le compte-rendu structuré ci-dessous.`,
    }));
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    if (!reportForm.findings.trim() && !reportForm.conclusion.trim()) return;
    if (selected.status === "scheduled") {
      updateImagingExamStatus(selected.id, "in_progress");
    }
    saveImagingReport(selected.id, { ...reportForm, reportFileName: fileName });
    const patient = getPatientById(selected.patientId);
    void notifyImagingReportReady({
      examId: selected.id,
      examNumber: selected.examNumber,
      examType: selected.examType,
      patientName: patient ? patientFullName(patient) : "Patient",
    });
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Comptes-rendus imagerie"
        description="Comptes-rendus structurés : technique, constatations, conclusion."
        actions={
          <Link to={routes.imaging} className="btn btn-outline-secondary btn-sm">
            Planning examens
          </Link>
        }
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-4">
          <div className="ph-invoice-sheet mb-3">
            <div className="ph-invoice-section-title">
              En attente de CR ({awaitingReport.length})
            </div>
            {awaitingReport.length === 0 ? (
              <p className="text-muted small mb-0">Aucun examen en cours.</p>
            ) : (
              <>
              <ul className="list-unstyled mb-0 small">
                {pagedAwaiting.map((exam) => (
                  <li key={exam.id} className="mb-2 pb-2 border-bottom">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-start"
                      onClick={() => openExam(exam)}
                    >
                      {exam.examNumber} — {exam.examType}
                    </button>
                  </li>
                ))}
              </ul>
              <TablePagination
                page={awaitingPagination.page}
                pageSize={awaitingPagination.pageSize}
                totalItems={awaitingPagination.totalItems}
                onPageChange={awaitingPagination.setPage}
                onPageSizeChange={awaitingPagination.setPageSize}
              />
              </>
            )}
          </div>

          <div className="ph-invoice-sheet">
            <div className="ph-invoice-section-title">Archives ({withReport.length})</div>
            {withReport.length === 0 ? (
              <p className="text-muted small mb-0">Aucun CR enregistré.</p>
            ) : (
              <>
              <ul className="list-unstyled mb-0 small">
                {pagedArchives.map((exam) => {
                  const patient = getPatientById(exam.patientId);
                  return (
                    <li key={exam.id} className="mb-2 pb-2 border-bottom">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-start"
                        onClick={() => openExam(exam)}
                      >
                        {exam.examNumber} — {patient ? patientFullName(patient) : "—"}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <TablePagination
                page={archivesPagination.page}
                pageSize={archivesPagination.pageSize}
                totalItems={archivesPagination.totalItems}
                onPageChange={archivesPagination.setPage}
                onPageSizeChange={archivesPagination.setPageSize}
              />
              </>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          {!selected ? (
            <div className="ph-invoice-sheet">
              <p className="text-muted mb-0">
                Sélectionnez un examen pour rédiger ou consulter le compte-rendu.
              </p>
            </div>
          ) : (
            <div className="ph-invoice-sheet">
              <div className="d-flex justify-content-between align-items-start mb-3 ph-split-detail-head">
                <div>
                  <span className="ph-inv-badge">{selected.examNumber}</span>
                  <h3 className="h6 fw-semibold mt-2 mb-0">
                    {selected.examType}
                    {selected.bodyPart ? ` — ${selected.bodyPart}` : ""}
                  </h3>
                  <p className="small text-muted mb-0">
                    {getPatientById(selected.patientId)
                      ? patientFullName(getPatientById(selected.patientId)!)
                      : "—"}
                    {selected.prescriber ? ` · Prescrit par ${selected.prescriber}` : ""}
                  </p>
                  {selected.clinicalIndication && (
                    <p className="small text-muted mb-0">Indication : {selected.clinicalIndication}</p>
                  )}
                  {imagingExamConsultationLabel(selected.consultationId) && (
                    <p className="small text-muted mb-0">
                      {imagingExamConsultationLabel(selected.consultationId)}
                    </p>
                  )}
                </div>
                <span className={`hosp-diag-badge hosp-diag-badge--${selected.status}`}>
                  {IMAGING_STATUS_LABELS[selected.status]}
                </span>
              </div>

              {selected.status !== "completed" && (
                <>
                  <label className="form-label small fw-semibold" htmlFor="img-file">
                    Importer un fichier (.txt)
                  </label>
                  <input
                    id="img-file"
                    type="file"
                    className="form-control form-control-sm mb-3"
                    accept=".txt,text/plain"
                    onChange={handleFileUpload}
                  />
                </>
              )}

              {selected.status === "completed" ? (
                <>
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => printImagingReport(selected)}
                    >
                      Imprimer / PDF
                    </button>
                  </div>
                  <div className="border rounded p-3 bg-light" style={{ whiteSpace: "pre-wrap" }}>
                    {selected.reportFileName && (
                      <p className="small text-muted mb-2">Fichier : {selected.reportFileName}</p>
                    )}
                    {formatImagingReportSummary(selected)}
                  </div>
                </>
              ) : (
                <form onSubmit={handleSave}>
                  <ImagingReportFields
                    form={reportForm}
                    canEdit
                    onChange={(patch) => setReportForm((prev) => ({ ...prev, ...patch }))}
                  />
                  <button type="submit" className="btn btn-primary btn-sm mt-3">
                    Enregistrer le CR
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
